import './LoginForm.css';
import { useState } from "react";
import { IAppUser } from "../../../models/IAppUser";
import Loader from "../../Loader/Loader";
import { useMsal } from "@azure/msal-react";
import { logger } from "../../../Services/LoggingService";
import { loginAPI } from "../../../api/loginAPI";
import { Link } from 'react-router-dom';
import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from "../../../Services/AppInsightService";
import { LoginAuthType } from '../../../models/enums';
import { toastService } from '../../../Services/ToastService';

interface ILoginFormProps {
    user: IAppUser;
    userAuthenticated: boolean
    loginCallback: (user: IAppUser) => void;
    logoutCallback: () => void;
}

const LoginForm: React.FC<ILoginFormProps> = ({ user, userAuthenticated, loginCallback, logoutCallback }) => {
    const { instance: msInst } = useMsal();
    let account = msInst.getActiveAccount();

    const [login, setLogin] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [isLoading, setIsLoading] = useState(false);

    const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

    const onHandleChange = (control: HTMLInputElement) => {
        if (control.name === "login") {
            setLogin(control.value);
        }
        if (control.name === "email") {
            setEmail(control.value);
        }
    };

    const onForgotClick = () => {
        setIsLoading(true);

        logger.info("Making ForgotPassword call.");
        loginAPI.getForgotPass(email)
        .then((ret) => {
            if (ret === null) {
                logger.error("Error processing ForgotPassword.");
            } else {
                logger.info("ForgotPassword call complete.", ret);
                toastService.success("Forgot password request has been processed.");
            }
        })
        .catch((err) => {
            logger.error("Error processing ForgotPassword:", err);
        })
        .finally(() => {
            setIsLoading(false);
        });
    };

    const onLoginClick = () => {
        setIsLoading(true);
        let strLogin = "";
        let strEmail = "";

        if (account) {
            // MS Login, or Google
            strLogin = account.name === undefined ? "" : account.name;
            strEmail = account.username;
        } else {
            // Form Login
            strLogin = login;
            strEmail = email;
            user.AuthType = LoginAuthType.Email;
        }

        user.Name = strLogin;
        user.Email = strEmail;

        logger.info("Making login call.");
        loginAPI.postLogin(user)
        .then((ret: IAppUser) => {
            if (ret === null || (ret.Id === null) || (ret.Id < 1)) {
                logger.warning("Error logging in.");
                user = {Id: -1};
                loginCallback(user);
            } else {
                if (login !== ret.Name) {
                    setLogin(ret.Name as string);
                };

                loginCallback(ret);
            }
        })
        .catch((err) => {
            logger.warning("Error making login call:", err);
            user = {Id: -1};
        })
        .finally(() => {
            setIsLoading(false);
        });
    };

    const onLogOutClick = () => {
        logger.info("Starting logout process.");
        setIsLoading(true);
        if (msInst.getActiveAccount()) {
            msInst.logoutPopup();
        }

        logoutCallback();
        setIsLoading(false);
    };

    const onMsSignInClick = async () => {
        setIsLoading(true);
        let hadError: boolean = false;

        msInst.loginPopup().catch(() => {
            user = {Id: -1};
            hadError = true;
        });

        // Wait for login process to finiah or error
        do {
            await delay(3000);
            account = msInst.getActiveAccount();
        } while (!hadError && account === null);

        // Now we know if we can login or not
        if (hadError === false) {
            user.AuthType = LoginAuthType.MicroSoft;
            user.Password = "";
            if (account?.name !== null && account?.name !== undefined) {
                setLogin(account?.name as string);
            }

            if (account?.username !== null && account?.username !== undefined) {
                setEmail(account?.username as string);
            }

            onLoginClick();
        } else {
            user = {Id: -1};
            setIsLoading(false);
        }
    };

    const ongoogleSignInClick = async () => {
        logger.error("Google login is not setup yet.");
        user.AuthType = LoginAuthType.Google;
        user.Password = "";
    };

    if (userAuthenticated) {
        return (
            <>
            {isLoading ? <Loader text='Logging out ...' /> :
                <div style={{ width: 150, display: "flex", flexDirection: "column", textDecoration: "none" }}>
                    <Link to="/characters">
                        <div className='login-link-content'>
                            <div className='login-link-image'>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" width="20" height="20"><path d="M144 0a80 80 0 1 1 0 160A80 80 0 1 1 144 0zM512 0a80 80 0 1 1 0 160A80 80 0 1 1 512 0zM0 298.7C0 239.8 47.8 192 106.7 192l42.7 0c15.9 0 31 3.5 44.6 9.7c-1.3 7.2-1.9 14.7-1.9 22.3c0 38.2 16.8 72.5 43.3 96c-.2 0-.4 0-.7 0L21.3 320C9.6 320 0 310.4 0 298.7zM405.3 320c-.2 0-.4 0-.7 0c26.6-23.5 43.3-57.8 43.3-96c0-7.6-.7-15-1.9-22.3c13.6-6.3 28.7-9.7 44.6-9.7l42.7 0C592.2 192 640 239.8 640 298.7c0 11.8-9.6 21.3-21.3 21.3l-213.3 0zM224 224a96 96 0 1 1 192 0 96 96 0 1 1 -192 0zM128 485.3C128 411.7 187.7 352 261.3 352l117.3 0C452.3 352 512 411.7 512 485.3c0 14.7-11.9 26.7-26.7 26.7l-330.7 0c-14.7 0-26.7-11.9-26.7-26.7z"/></svg>
                            </div>
                            <div>
                                My Adventurers
                            </div>
                        </div>
                    </Link>
                    <Link to="/account">
                        <div className='login-link-content'>
                            <div className='login-link-image'>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" width="20" height="20"><path d="M308.5 135.3c7.1-6.3 9.9-16.2 6.2-25c-2.3-5.3-4.8-10.5-7.6-15.5L304 89.4c-3-5-6.3-9.9-9.8-14.6c-5.7-7.6-15.7-10.1-24.7-7.1l-28.2 9.3c-10.7-8.8-23-16-36.2-20.9L199 27.1c-1.9-9.3-9.1-16.7-18.5-17.8C173.9 8.4 167.2 8 160.4 8l-.7 0c-6.8 0-13.5 .4-20.1 1.2c-9.4 1.1-16.6 8.6-18.5 17.8L115 56.1c-13.3 5-25.5 12.1-36.2 20.9L50.5 67.8c-9-3-19-.5-24.7 7.1c-3.5 4.7-6.8 9.6-9.9 14.6l-3 5.3c-2.8 5-5.3 10.2-7.6 15.6c-3.7 8.7-.9 18.6 6.2 25l22.2 19.8C32.6 161.9 32 168.9 32 176s.6 14.1 1.7 20.9L11.5 216.7c-7.1 6.3-9.9 16.2-6.2 25c2.3 5.3 4.8 10.5 7.6 15.6l3 5.2c3 5.1 6.3 9.9 9.9 14.6c5.7 7.6 15.7 10.1 24.7 7.1l28.2-9.3c10.7 8.8 23 16 36.2 20.9l6.1 29.1c1.9 9.3 9.1 16.7 18.5 17.8c6.7 .8 13.5 1.2 20.4 1.2s13.7-.4 20.4-1.2c9.4-1.1 16.6-8.6 18.5-17.8l6.1-29.1c13.3-5 25.5-12.1 36.2-20.9l28.2 9.3c9 3 19 .5 24.7-7.1c3.5-4.7 6.8-9.5 9.8-14.6l3.1-5.4c2.8-5 5.3-10.2 7.6-15.5c3.7-8.7 .9-18.6-6.2-25l-22.2-19.8c1.1-6.8 1.7-13.8 1.7-20.9s-.6-14.1-1.7-20.9l22.2-19.8zM112 176a48 48 0 1 1 96 0 48 48 0 1 1 -96 0zM504.7 500.5c6.3 7.1 16.2 9.9 25 6.2c5.3-2.3 10.5-4.8 15.5-7.6l5.4-3.1c5-3 9.9-6.3 14.6-9.8c7.6-5.7 10.1-15.7 7.1-24.7l-9.3-28.2c8.8-10.7 16-23 20.9-36.2l29.1-6.1c9.3-1.9 16.7-9.1 17.8-18.5c.8-6.7 1.2-13.5 1.2-20.4s-.4-13.7-1.2-20.4c-1.1-9.4-8.6-16.6-17.8-18.5L583.9 307c-5-13.3-12.1-25.5-20.9-36.2l9.3-28.2c3-9 .5-19-7.1-24.7c-4.7-3.5-9.6-6.8-14.6-9.9l-5.3-3c-5-2.8-10.2-5.3-15.6-7.6c-8.7-3.7-18.6-.9-25 6.2l-19.8 22.2c-6.8-1.1-13.8-1.7-20.9-1.7s-14.1 .6-20.9 1.7l-19.8-22.2c-6.3-7.1-16.2-9.9-25-6.2c-5.3 2.3-10.5 4.8-15.6 7.6l-5.2 3c-5.1 3-9.9 6.3-14.6 9.9c-7.6 5.7-10.1 15.7-7.1 24.7l9.3 28.2c-8.8 10.7-16 23-20.9 36.2L315.1 313c-9.3 1.9-16.7 9.1-17.8 18.5c-.8 6.7-1.2 13.5-1.2 20.4s.4 13.7 1.2 20.4c1.1 9.4 8.6 16.6 17.8 18.5l29.1 6.1c5 13.3 12.1 25.5 20.9 36.2l-9.3 28.2c-3 9-.5 19 7.1 24.7c4.7 3.5 9.5 6.8 14.6 9.8l5.4 3.1c5 2.8 10.2 5.3 15.5 7.6c8.7 3.7 18.6 .9 25-6.2l19.8-22.2c6.8 1.1 13.8 1.7 20.9 1.7s14.1-.6 20.9-1.7l19.8 22.2zM464 304a48 48 0 1 1 0 96 48 48 0 1 1 0-96z"/></svg>
                            </div>
                            <div>
                                My Account
                            </div>
                        </div>
                    </Link>
                    <br />
                    <button className="login-btn-plain" style={{ width: "100%" }} onClick={onLogOutClick}>
                        <div className='login-btn-plain-content'>
                            <div className='login-btn-plain-image'>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path d="M272 0a48 48 0 1 1 0 96 48 48 0 1 1 0-96zM163.2 160c-14.1 0-27.5 5.6-37.5 15.5l-23.1 23.1c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3l23.1-23.1C102.4 108.3 132.2 96 163.2 96c11.7 0 23.3 1.7 34.4 5.2l48.7 15c36.4 11.2 65.9 38 80.5 73.1l6.3 15c5 11.9 16.6 19.7 29.5 19.7l21.3 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-21.3 0c-38.8 0-73.7-23.3-88.6-59.1l-5.1-12.3L238 299l56.7 35.4c18.1 11.3 26.6 33.3 20.7 53.9L286.8 488.8c-4.9 17-22.6 26.8-39.6 22s-26.8-22.6-22-39.6l25.4-88.8-86.2-53.9c-25.1-15.7-36.1-46.5-26.8-74.5l31.2-93.7c-1.9-.2-3.7-.3-5.6-.3zM109.5 310.7c7.4 18.1 20.4 33.9 37.9 44.9l9.5 6-11.5 25.9c-7.7 17.3-24.9 28.5-43.9 28.5L32 416c-17.7 0-32-14.3-32-32s14.3-32 32-32l59.2 0 18.3-41.3zM384 48c0-26.5 21.5-48 48-48l96 0c26.5 0 48 21.5 48 48l0 416c0 26.5-21.5 48-48 48l-96 0c-26.5 0-48-21.5-48-48l0-144s0 0 0 0c35.3 0 64-28.7 64-64s-28.7-64-64-64c0 0 0 0 0 0l0-144z"/></svg>
                            </div>
                            <div className='login-btn-plain-text'>
                                Logout
                            </div>
                        </div>
                    </button>
                </div>
            }</>
        );
    } else {
        return (
            <div style={{ width: 250 }}>
                {isLoading ? <Loader text='Logging in ...' /> :
                <div style={{ display: "flex", flexDirection: "column"}}>
                    <div style={{ width: "100%", textAlign: "left" }}>Login</div>
                    {/* Login top section - 3rd part services */}
                    <div>
                        <button className="login-btn-plain" style={{ width: "80%" }} onClick={onMsSignInClick}>
                            <div className='login-btn-plain-content'>
                                <div className='login-btn-plain-image'>
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="20" height="20"><path d="M0 0h15.206v15.206H0z" fill="#f25022"/><path d="M16.794 0H32v15.206H16.794z" fill="#7fba00"/><path d="M0 16.794h15.206V32H0z" fill="#00a4ef"/><path d="M16.794 16.794H32V32H16.794z" fill="#ffb900"/></svg>
                                </div>
                                <div className='login-btn-plain-text'>
                                    Login with Microsoft
                                </div>
                            </div>
                        </button>
                        <button className="login-btn-plain" style={{  width: "80%" }} onClick={ongoogleSignInClick}>
                            <div className='login-btn-plain-content'>
                                <div className='login-btn-plain-image'>
                                    <svg aria-hidden="true" width="20" height="20" viewBox="0 0 16 16">
                                        <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18"></path>
                                        <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17"></path>
                                        <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18z"></path>
                                        <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.8 4.8 0 0 1 4.48-3.3"></path>
                                    </svg>
                                </div>
                                <div className='login-btn-plain-text'>
                                    Login with Google
                                </div>
                            </div>
                        </button>
                    </div>

                    {/* Login bottom section - email and pass */}
                    <div style={{ padding: "15px" }}>
                        ------------------ or ------------------<br />
                        <input style={{ width: "190px", margin: "5px 0 0 0" }} name='login' placeholder="Enter your Login name" value={login} onChange={(event) => onHandleChange(event.target)} />
                        <input style={{ width: "190px", margin: "5px 0 0px 0" }} name='email' placeholder="Enter your Email" value={email} onChange={(event) => onHandleChange(event.target)} />
                        <button className="login-btn-plain" style={{ width: "90%" }} onClick={onLoginClick}>
                            <div className='login-btn-plain-content'>
                                <div className='login-btn-plain-image'>
                                    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 18 18">
                                        <path fill="#BCBBBB" d="M14 16v-5h2v7H2v-7h2v5z"></path>
                                        <path fill="#F48024" d="m12.09.72-1.21.9 4.5 6.07 1.22-.9zM5 15h8v-2H5zm9.15-5.87L8.35 4.3l.96-1.16 5.8 4.83zm-7.7-1.47 6.85 3.19.63-1.37-6.85-3.2zm6.53 5L5.4 11.39l.38-1.67 7.42 1.48z"></path>
                                    </svg>
                                </div>
                                <div className='login-btn-plain-text'>
                                    Login with email
                                </div>
                            </div>
                        </button>
                    </div>

                    <div>
                        Register &nbsp; | &nbsp; <span onClick={() => {onForgotClick()}}>Forgot Password</span>
                    </div>
                </div>
                }

            </div>
        );
    }
}

export default withAITracking(reactPlugin, LoginForm);
